import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactPlayer from "react-player";
import { 
  Pause, 
  Volume2, 
  VolumeX, 
  Maximize2, 
  Minimize2,
  RotateCcw,
  Loader2,
  FastForward
} from "lucide-react";
import { trackVideoPlayerAction } from "util/analytics";
import api from "config/axiosConfig";
import { apiRoutes } from "util/apiRoutes";
import { videoPlayerStateStore } from "util/videoPlayerState";

// Custom Triangular Play Button Component
const TriangularPlayButton: React.FC<{ className?: string }> = ({ className = "" }) => (
  <svg
    viewBox="0 0 100 100"
    className={className}
    fill="currentColor"
  >
    <polygon
      points="25,15 25,85 75,50"
      style={{
        fill: "currentColor",
        stroke: "none"
      }}
    />
  </svg>
);

type IOSVideoFullscreenApi = {
  webkitEnterFullscreen?: () => void;
  webkitEnterFullScreen?: () => void;
  webkitExitFullscreen?: () => void;
  webkitRequestFullscreen?: () => void;
};

type VendorFullscreenElementApi = {
  // Standard
  requestFullscreen?: () => Promise<void> | void;
  // WebKit (Safari, some older Android WebViews)
  webkitRequestFullscreen?: () => Promise<void> | void;
  // Gecko (older Firefox)
  mozRequestFullScreen?: () => Promise<void> | void;
  // MS (legacy Edge/IE)
  msRequestFullscreen?: () => Promise<void> | void;
};

type DocumentWebkitFullscreenApi = {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element;
};

type DocumentVendorFullscreenApi = DocumentWebkitFullscreenApi & {
  // Standard
  exitFullscreen?: () => Promise<void> | void;
  fullscreenElement?: Element | null;
  // Gecko (older Firefox)
  mozCancelFullScreen?: () => Promise<void> | void;
  mozFullScreenElement?: Element;
  // MS (legacy Edge/IE)
  msExitFullscreen?: () => Promise<void> | void;
  msFullscreenElement?: Element;
};

/**
 * Safely narrows an unknown value to an HTMLVideoElement.
 * Avoids unsafe casts and keeps TS strict without `any`.
 */
function isHTMLVideoElement(value: unknown): value is HTMLVideoElement {
  if (typeof value !== "object" || value === null) return false;
  if (!("tagName" in value)) return false;
  const tagName = (value as { tagName?: unknown }).tagName;
  return typeof tagName === "string" && tagName.toUpperCase() === "VIDEO";
}

/**
 * Safely narrows an unknown value to an HTMLIFrameElement.
 * Useful for ReactPlayer's YouTube/Vimeo/etc iframe-backed players.
 */
function isHTMLIFrameElement(value: unknown): value is HTMLIFrameElement {
  if (typeof value !== "object" || value === null) return false;
  if (!("tagName" in value)) return false;
  const tagName = (value as { tagName?: unknown }).tagName;
  return typeof tagName === "string" && tagName.toUpperCase() === "IFRAME";
}

/**
 * Returns the internal <video> element when ReactPlayer is using the HTML5 player.
 */
function getInternalVideoElement(player: ReactPlayer | null): HTMLVideoElement | null {
  if (!player) return null;
  const internal = player.getInternalPlayer();
  return isHTMLVideoElement(internal) ? internal : null;
}

/**
 * Returns the internal <iframe> element when ReactPlayer is using an iframe-based player.
 */
function getInternalIFrameElement(player: ReactPlayer | null): HTMLIFrameElement | null {
  if (!player) return null;
  const internal = player.getInternalPlayer();
  return isHTMLIFrameElement(internal) ? internal : null;
}

interface VideoPlayerProps {
  url: string;
  thumbnail?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  width?: string | number;
  height?: string | number;
  maxHeight?: string;
  objectFit?: "contain" | "cover" | "fill";
  /**
   * When true, clicking the video container does NOT enter fullscreen.
   * Instead, clicks toggle play/pause when out of fullscreen, and the
   * caller's `onClick` handler can capture taps for surfacing flows like
   * opening a media viewer. The explicit fullscreen button still works.
   */
  disableClickFullscreen?: boolean;
  /**
   * Whether the video should pause when it is not the active item in a
   * carousel. When this transitions back to true, the player auto-resumes
   * unless the user explicitly paused.
   */
  isActive?: boolean;
  /**
   * When true, the player reads/writes its playback state (currentTime,
   * playbackRate, volume, muted, userPaused) into a global store keyed by
   * the video URL. Multiple instances of the same URL stay in sync, which
   * lets the carousel video and the MediaViewer video resume seamlessly
   * from each other.
   */
  shareStateAcrossInstances?: boolean;
}

interface SignedMediaRefreshResponse {
  key?: string;
  url?: string;
  signed_url?: string;
  expires_at?: string;
}

const CF_SIGNATURE_PARAM_PATTERN = /[?&](Expires|Policy|Signature|Key-Pair-Id)=/i;

function hasSignedCloudFrontQuery(value: string): boolean {
  return CF_SIGNATURE_PARAM_PATTERN.test(String(value || ""));
}

function extractMediaKeyFromUrl(value: string): string {
  const rawValue = String(value || "").trim();
  if (!rawValue) {
    return "";
  }
  try {
    const parsed = new URL(rawValue);
    return parsed.pathname.replace(/^\/+/, "");
  } catch {
    return rawValue.replace(/^https?:\/\/[^/]+\/?/, "").replace(/^\/+/, "");
  }
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  url,
  thumbnail,
  autoPlay = false,
  muted = true,
  loop = false,
  controls = true,
  className = "",
  onClick,
  width = "100%",
  height = "100%",
  maxHeight = "",
  objectFit = "contain",
  disableClickFullscreen = false,
  isActive = true,
  shareStateAcrossInstances = false
}) => {
  // Read any cached state for this URL so a second VideoPlayer mounting on
  // the same media (e.g., MediaCarousel → MediaViewer transition) starts
  // exactly where the previous instance left off.
  const initialSnapshot = useRef(
    shareStateAcrossInstances
      ? videoPlayerStateStore.getSnapshot(url)
      : null,
  ).current;

  const [playing, setPlaying] = useState(
    autoPlay && !(initialSnapshot?.userPaused ?? false),
  );
  const [volume, setVolume] = useState(
    initialSnapshot?.volume ?? (muted ? 0 : 0.8),
  );
  const [isMuted, setIsMuted] = useState(initialSnapshot?.isMuted ?? muted);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(
    initialSnapshot?.currentTime ?? 0,
  );
  const [showControls, setShowControls] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  // Track if user manually paused (vs paused due to inactive carousel slide).
  const [userPaused, setUserPaused] = useState(
    initialSnapshot?.userPaused ?? false,
  );
  const [playbackRate, setPlaybackRate] = useState(
    initialSnapshot?.playbackRate ?? 1,
  );
  const [retryKey, setRetryKey] = useState(0); // Force remount on retry
  const [resolvedUrl, setResolvedUrl] = useState(url);
  // Tracks whether we have already seeked to the cached `currentTime` after
  // the player became ready. Prevents seeking on every state update.
  const initialSeekAppliedRef = useRef<boolean>(false);
  
  const playerRef = useRef<ReactPlayer>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>();
  const resumeAfterExitTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const signedUrlRefreshAttemptedRef = useRef(false);

  /**
   * Mirrors current `playing` state into a ref so fullscreen helpers can read it
   * without adding `playing` as a dependency everywhere.
   */
  const playingRef = useRef<boolean>(playing);
  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  /**
   * iOS Safari may forcibly pause media when leaving fullscreen.
   * Track whether we *should* resume after exit based on whether the video
   * was playing at the moment fullscreen was entered.
   */
  const shouldResumeAfterFullscreenExitRef = useRef<boolean>(false);

  useEffect(() => {
    setResolvedUrl(url);
    signedUrlRefreshAttemptedRef.current = false;
  }, [url]);

  const tryRefreshSignedUrl = useCallback(async (): Promise<boolean> => {
    if (signedUrlRefreshAttemptedRef.current) {
      return false;
    }
    if (!hasSignedCloudFrontQuery(resolvedUrl)) {
      return false;
    }

    const mediaKey = extractMediaKeyFromUrl(resolvedUrl);
    if (!mediaKey) {
      return false;
    }

    signedUrlRefreshAttemptedRef.current = true;
    try {
      const response = await api.post<SignedMediaRefreshResponse>(apiRoutes.media.sign, {
        key: mediaKey,
      });
      const refreshedUrl = String(response.data?.signed_url || response.data?.url || "").trim();
      if (!refreshedUrl) {
        return false;
      }

      setResolvedUrl(refreshedUrl);
      setRetryKey((previousKey) => previousKey + 1);
      setHasError(false);
      setIsLoading(true);
      setIsReady(false);
      return true;
    } catch {
      return false;
    }
  }, [resolvedUrl]);

  // Show controls temporarily when interacting
  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  }, []);

  // Handle progress bar click
  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newProgress = (clickX / rect.width);
    const newTime = newProgress * duration;
    const oldTime = currentTime;
    
    setIsSeeking(true);
    playerRef.current?.seekTo(newProgress);
    setCurrentTime(newTime);
    setProgress(newProgress);
    
    // Track seek action
    trackVideoPlayerAction.seek(resolvedUrl, oldTime, newTime);
    
    // Reset seeking state after a short delay
    setTimeout(() => setIsSeeking(false), 100);
  }, [duration, currentTime, resolvedUrl]);

  // Toggle mute
  const toggleMute = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    setVolume(newMutedState ? 0 : 0.8);
    
    // Track volume toggle action
    trackVideoPlayerAction.volumeToggle(resolvedUrl, newMutedState ? "muted" : "unmuted");
    
    showControlsTemporarily();
  }, [isMuted, showControlsTemporarily, resolvedUrl]);

  /**
   * Restart the video from the beginning without changing the user's play/pause intent.
   * - If currently playing, it keeps playing from 0.
   * - If paused, it remains paused at 0.
   */
  const restartVideo = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const oldTime = currentTime;
    const newTime = 0;

    setIsSeeking(true);
    playerRef.current?.seekTo(newTime, "seconds");
    setCurrentTime(newTime);
    setProgress(0);

    // Track restart as a seek-to-zero for analytics consistency.
    trackVideoPlayerAction.seek(resolvedUrl, oldTime, newTime);

    showControlsTemporarily();

    // Reset seeking state after a short delay
    setTimeout(() => setIsSeeking(false), 100);
  }, [currentTime, showControlsTemporarily, resolvedUrl]);

  // Toggle fullscreen
  /**
   * Requests fullscreen entry/exit using a cross-browser strategy.
   * This intentionally does NOT require an event argument so it can be invoked
   * from either the container click or the fullscreen button click.
   */
  const requestFullscreenToggle = useCallback(async (): Promise<void> => {
    const container = containerRef.current;
    if (!container) return;

    const vendorDocument = document as DocumentVendorFullscreenApi;
    const currentFullscreenElement =
      vendorDocument.fullscreenElement ??
      vendorDocument.webkitFullscreenElement ??
      vendorDocument.mozFullScreenElement ??
      vendorDocument.msFullscreenElement ??
      null;
    const isEnteringFullscreen = !currentFullscreenElement;

    const internalVideo = getInternalVideoElement(playerRef.current);
    const internalIFrame = getInternalIFrameElement(playerRef.current);

    /**
     * iPhone Safari does not support Element.requestFullscreen for arbitrary elements.
     * Instead, fullscreen must be requested on the <video> element via webkitEnterFullscreen.
     */
    const tryEnterIOSVideoFullscreen = (): boolean => {
      if (!internalVideo) return false;
      const iosApi = internalVideo as HTMLVideoElement & IOSVideoFullscreenApi;
      if (typeof iosApi.webkitEnterFullscreen === "function") {
        iosApi.webkitEnterFullscreen();
        return true;
      }
      if (typeof iosApi.webkitEnterFullScreen === "function") {
        iosApi.webkitEnterFullScreen();
        return true;
      }
      if (typeof iosApi.webkitRequestFullscreen === "function") {
        iosApi.webkitRequestFullscreen();
        return true;
      }
      if (typeof internalVideo.requestFullscreen === "function") {
        void internalVideo.requestFullscreen();
        return true;
      }
      return false;
    };

    const tryEnterFullscreenOnElement = async (el: Element): Promise<boolean> => {
      const vendorEl = el as Element & VendorFullscreenElementApi;

      // Standard API first (Chrome/Android/modern Safari/modern Firefox)
      if (typeof vendorEl.requestFullscreen === "function") {
        await vendorEl.requestFullscreen();
        return true;
      }
      // Vendor-prefixed fallbacks
      if (typeof vendorEl.webkitRequestFullscreen === "function") {
        await vendorEl.webkitRequestFullscreen();
        return true;
      }
      if (typeof vendorEl.mozRequestFullScreen === "function") {
        await vendorEl.mozRequestFullScreen();
        return true;
      }
      if (typeof vendorEl.msRequestFullscreen === "function") {
        await vendorEl.msRequestFullscreen();
        return true;
      }
      return false;
    };

    const tryExitFullscreen = async (): Promise<boolean> => {
      // Standard + vendor-prefixed document exit APIs
      if (typeof vendorDocument.exitFullscreen === "function" && vendorDocument.fullscreenElement) {
        await vendorDocument.exitFullscreen();
        return true;
      }
      if (typeof vendorDocument.webkitExitFullscreen === "function" && vendorDocument.webkitFullscreenElement) {
        await vendorDocument.webkitExitFullscreen();
        return true;
      }
      if (typeof vendorDocument.mozCancelFullScreen === "function" && vendorDocument.mozFullScreenElement) {
        await vendorDocument.mozCancelFullScreen();
        return true;
      }
      if (typeof vendorDocument.msExitFullscreen === "function" && vendorDocument.msFullscreenElement) {
        await vendorDocument.msExitFullscreen();
        return true;
      }
      if (internalVideo) {
        const iosApi = internalVideo as HTMLVideoElement & IOSVideoFullscreenApi;
        if (typeof iosApi.webkitExitFullscreen === "function") {
          iosApi.webkitExitFullscreen();
          return true;
        }
      }
      return false;
    };

    try {
      if (isEnteringFullscreen) {
        // Snapshot whether we should resume playback if the platform pauses on exit.
        shouldResumeAfterFullscreenExitRef.current = playingRef.current;

        /**
         * Strategy order:
         * 1) Fullscreen the container (best for custom controls overlay; works on desktop + most Android + iPadOS Safari)
         * 2) If ReactPlayer rendered an <iframe>, fullscreen the iframe (helps some embedded-player scenarios)
         * 3) If ReactPlayer rendered a <video>, fullscreen the video (works on many platforms; required for iPhone via WebKit APIs)
         */
        const entered =
          (await tryEnterFullscreenOnElement(container)) ||
          (internalIFrame ? await tryEnterFullscreenOnElement(internalIFrame) : false) ||
          (internalVideo ? await tryEnterFullscreenOnElement(internalVideo) : false) ||
          tryEnterIOSVideoFullscreen();

        if (entered) {
          trackVideoPlayerAction.fullscreenToggle(resolvedUrl, "enter");
        }
      } else {
        // User is exiting fullscreen explicitly: do not auto-resume unless it was playing on entry.
        const exited = await tryExitFullscreen();
        if (exited) {
          trackVideoPlayerAction.fullscreenToggle(resolvedUrl, "exit");
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "unknown";
      trackVideoPlayerAction.error(resolvedUrl, `fullscreen_toggle_failed:${message}`);
    } finally {
      showControlsTemporarily();
    }
  }, [showControlsTemporarily, resolvedUrl]);

  /**
   * Container click behavior:
   * - When NOT fullscreen: click enters fullscreen (instead of play/pause).
   * - When fullscreen: click toggles play/pause (expected UX in fullscreen).
   *
   * Important: Control buttons must call stopPropagation so they don't trigger this.
   */
  /**
   * Container click behavior depends on context:
   * - In fullscreen: click always toggles play/pause (expected UX).
   * - In a card carousel (`disableClickFullscreen=true` AND consumer
   *   provides `onClick`): forward the click so the consumer can open the
   *   media viewer; do NOT toggle play/pause and do NOT enter fullscreen.
   * - In the media viewer (`disableClickFullscreen=true` AND no `onClick`):
   *   click toggles play/pause, just like fullscreen, but does NOT enter
   *   the native fullscreen mode.
   * - Default (no `disableClickFullscreen`): click enters fullscreen.
   *
   * Control buttons must call stopPropagation so they don't trigger this.
   */
  const handleContainerClick = useCallback(async (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();

    if (!isFullscreen && !disableClickFullscreen) {
      await requestFullscreenToggle();
      onClick?.(e);
      return;
    }

    // disableClickFullscreen + onClick: forward to consumer (e.g., open
    // MediaViewer). Do not toggle play/pause from a card.
    if (!isFullscreen && disableClickFullscreen && onClick) {
      onClick(e);
      return;
    }

    // Fullscreen, OR (disableClickFullscreen + no onClick) — toggle play/pause.
    const newPlayingState = !playing;
    setPlaying(newPlayingState);
    setUserPaused(!newPlayingState);

    if (newPlayingState) {
      trackVideoPlayerAction.play(resolvedUrl, "click");
    } else {
      trackVideoPlayerAction.pause(resolvedUrl, "click", currentTime);
    }

    showControlsTemporarily();
    onClick?.(e);
  }, [
    currentTime,
    isFullscreen,
    disableClickFullscreen,
    onClick,
    playing,
    requestFullscreenToggle,
    showControlsTemporarily,
    resolvedUrl
  ]);

  // Toggle playback rate between 1x and 2x
  const togglePlaybackRate = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const oldRate = playbackRate;
    const newRate = playbackRate === 1 ? 2 : 1;
    setPlaybackRate(newRate);
    
    // Track speed change action
    trackVideoPlayerAction.speedChange(resolvedUrl, newRate, oldRate);
    
    showControlsTemporarily();
  }, [playbackRate, showControlsTemporarily, resolvedUrl]);

  // Format time display
  const formatTime = useCallback((seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  }, []);

  // Smooth progress animation using interval
  const updateProgressSmooth = useCallback(() => {
    if (playerRef.current && !isSeeking) {
      const currentTime = playerRef.current.getCurrentTime();
      const duration = playerRef.current.getDuration();
      
      if (duration > 0) {
        const newProgress = currentTime / duration;
        setProgress(newProgress);
        setCurrentTime(currentTime);
      }
    }
  }, [isSeeking]);

  // Handle player events
  const handleReady = useCallback(() => {
    setIsReady(true);
    setIsLoading(false);
    setHasError(false);

    // Seek to the cached `currentTime` (if any) once the player is ready.
    // Only do this once per mount so we do not fight the user's seeking.
    if (
      shareStateAcrossInstances &&
      !initialSeekAppliedRef.current &&
      initialSnapshot &&
      initialSnapshot.currentTime > 0 &&
      playerRef.current
    ) {
      initialSeekAppliedRef.current = true;
      try {
        playerRef.current.seekTo(initialSnapshot.currentTime, "seconds");
        setCurrentTime(initialSnapshot.currentTime);
      } catch {
        // Best-effort; ReactPlayer can fail seek before the underlying
        // <video> has buffered. The next `onProgress` will catch up.
      }
    }
  }, [shareStateAcrossInstances, initialSnapshot]);

  const handleError = useCallback((error?: unknown) => {
    const message = error instanceof Error ? error.message : "unknown";
    trackVideoPlayerAction.error(resolvedUrl, message);
    setIsLoading(false);

    if (hasSignedCloudFrontQuery(resolvedUrl) && !signedUrlRefreshAttemptedRef.current) {
      void (async () => {
        const refreshed = await tryRefreshSignedUrl();
        if (!refreshed) {
          setHasError(true);
        }
      })();
      return;
    }

    setHasError(true);
  }, [resolvedUrl, tryRefreshSignedUrl]);

  const handleProgress = useCallback((state: { played: number; playedSeconds: number }) => {
    // Only update from onProgress when not using smooth animation
    if (!playing || isSeeking) {
      setProgress(state.played);
      setCurrentTime(state.playedSeconds);
    }
  }, [playing, isSeeking]);

  const handleDuration = useCallback((duration: number) => {
    setDuration(duration);
  }, []);

  const handleBuffer = useCallback(() => {
    setIsLoading(true);
  }, []);

  const handleBufferEnd = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleEnded = useCallback(() => {
    // Track video completion
    trackVideoPlayerAction.ended(resolvedUrl, duration);
    
    if (loop) {
      // Reset progress and restart
      setProgress(0);
      setCurrentTime(0);
      setPlaying(true);
      setUserPaused(false); // Reset user pause state when looping
    }
  }, [loop, resolvedUrl, duration]);

  // Retry loading the video
  const retryLoading = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Track retry action
    trackVideoPlayerAction.retry(resolvedUrl);
    
    setHasError(false);
    setIsLoading(true);
    setIsReady(false);
    setPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    setUserPaused(false);
    // Force remount by incrementing key
    setRetryKey(prev => prev + 1);
  }, [resolvedUrl]);

  // Handle mouse movement to show controls
  const handleMouseMove = useCallback(() => {
    if (controls) {
      showControlsTemporarily();
    }
  }, [controls, showControlsTemporarily]);

  // Refs that mirror the latest state values so long-lived callbacks
  // (IntersectionObserver, MutationObserver) can read current values
  // without forcing themselves to re-create every render.
  const isActiveRef = useRef<boolean>(isActive);
  const playingRefForObserver = useRef<boolean>(playing);
  const isReadyRef = useRef<boolean>(isReady);
  const userPausedRef = useRef<boolean>(userPaused);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);
  useEffect(() => { playingRefForObserver.current = playing; }, [playing]);
  useEffect(() => { isReadyRef.current = isReady; }, [isReady]);
  useEffect(() => { userPausedRef.current = userPaused; }, [userPaused]);

  // Pause when this player becomes inactive in a carousel (e.g., user
  // swiped to the next slide). Auto-resume when it becomes active again
  // unless the user explicitly paused.
  useEffect(() => {
    if (!isReady) return;
    if (!isActive && playing) {
      setPlaying(false);
      trackVideoPlayerAction.pause(resolvedUrl, "viewport", currentTime);
      return;
    }
    if (isActive && !playing && !userPaused) {
      setPlaying(true);
      trackVideoPlayerAction.play(resolvedUrl, "viewport");
    }
    // Intentionally exclude `playing` from deps so this effect only runs
    // when isActive flips. Otherwise pausing manually would trigger an
    // immediate resume since !playing && !userPaused becomes true on the
    // next render before userPaused has settled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isReady, userPaused]);

  /**
   * Pause card-level video instances while a `FullscreenViewer` (e.g., the
   * `MediaViewer`) is open and this player is rendered OUTSIDE the viewer.
   * Without this guard, two `VideoPlayer`s sharing the same URL via
   * `videoPlayerStateStore` would play simultaneously and double-up audio.
   * The viewer's own player has `data-fullscreen-viewer="true"` on an
   * ancestor, so it skips the pause.
   */
  useEffect(() => {
    const body = document.body;
    const isInsideFullscreenViewer = (): boolean => {
      const node = containerRef.current;
      return node ? Boolean(node.closest('[data-fullscreen-viewer="true"]')) : false;
    };
    const updatePauseState = (): void => {
      const fullscreenViewerOpen = body.classList.contains(
        "fullscreen-viewer-active",
      );
      if (
        fullscreenViewerOpen &&
        !isInsideFullscreenViewer() &&
        playingRefForObserver.current
      ) {
        setPlaying(false);
      }
    };
    updatePauseState();
    const observer = new MutationObserver(updatePauseState);
    observer.observe(body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  // Persist state to the cross-instance store so a sibling VideoPlayer
  // (e.g., the MediaViewer's player when launched from a card) starts at
  // the same time, playback rate, volume, and pause state.
  useEffect(() => {
    if (!shareStateAcrossInstances) return;
    videoPlayerStateStore.setSnapshot(url, {
      currentTime,
      playbackRate,
      volume,
      isMuted,
      userPaused,
    });
  }, [
    shareStateAcrossInstances,
    url,
    currentTime,
    playbackRate,
    volume,
    isMuted,
    userPaused,
  ]);

  useEffect(() => {
    if (!containerRef.current) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting && entry.intersectionRatio > 0.5;

        if (
          isVisible &&
          !playingRefForObserver.current &&
          isReadyRef.current &&
          !userPausedRef.current &&
          isActiveRef.current
        ) {
          // Auto-play when in viewport (and active in carousel) only if
          // the user hasn't manually paused.
          setPlaying(true);
          trackVideoPlayerAction.play(resolvedUrl, "viewport");
        } else if (!isVisible && playingRefForObserver.current) {
          // Pause when out of viewport. Reset userPaused so that on the
          // next viewport entry we autoplay again (matches the existing
          // infinite-feed behavior).
          setPlaying(false);
          setUserPaused(false);
          trackVideoPlayerAction.pause(resolvedUrl, "viewport", currentTime);
        }
      },
      {
        threshold: [0, 0.5, 1],
        rootMargin: "0px"
      }
    );

    observerRef.current.observe(containerRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [resolvedUrl, currentTime]);

  // Start/stop smooth progress animation with 100ms intervals
  useEffect(() => {
    if (playing && isReady && !isSeeking) {
      // Start smooth animation every 100ms
      progressIntervalRef.current = setInterval(updateProgressSmooth, 100);
    } else {
      // Stop smooth animation
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [playing, isReady, isSeeking, updateProgressSmooth]);

  // Cleanup timeout and interval on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (resumeAfterExitTimeoutRef.current) {
        clearTimeout(resumeAfterExitTimeoutRef.current);
      }
    };
  }, []);

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      const vendorDocument = document as DocumentVendorFullscreenApi;
      setIsFullscreen(
        Boolean(
          vendorDocument.fullscreenElement ??
            vendorDocument.webkitFullscreenElement ??
            vendorDocument.mozFullScreenElement ??
            vendorDocument.msFullscreenElement
        )
      );
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    // Safari (WebKit) uses a vendor-prefixed fullscreenchange event in some versions.
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange as EventListener);
    // Legacy vendor events (rare, but harmless to include).
    document.addEventListener("mozfullscreenchange", handleFullscreenChange as EventListener);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange as EventListener);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange as EventListener);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange as EventListener);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange as EventListener);
    };
  }, []);

  // iOS video fullscreen events (these do NOT reliably trigger document fullscreenchange on iPhone).
  useEffect(() => {
    const video = getInternalVideoElement(playerRef.current);
    if (!video) return;

    const handleBegin = () => setIsFullscreen(true);
    const handleEnd = () => {
      setIsFullscreen(false);

      // On iOS, leaving fullscreen often pauses the video automatically.
      // If it was playing when fullscreen began, attempt to resume.
      if (shouldResumeAfterFullscreenExitRef.current) {
        // Small delay helps iOS settle state after fullscreen teardown.
        if (resumeAfterExitTimeoutRef.current) {
          clearTimeout(resumeAfterExitTimeoutRef.current);
        }
        resumeAfterExitTimeoutRef.current = setTimeout(() => {
          setUserPaused(false);
          setPlaying(true);

          // Best-effort direct resume for HTML5 video (may be blocked in some WebViews).
          const playPromise = video.play();
          if (playPromise instanceof Promise) {
            playPromise.catch(() => {
              // If autoplay is blocked, ReactPlayer state is still consistent and user can tap play.
              setPlaying(false);
            });
          }
        }, 75);
      }
    };

    video.addEventListener("webkitbeginfullscreen", handleBegin);
    video.addEventListener("webkitendfullscreen", handleEnd);

    return () => {
      video.removeEventListener("webkitbeginfullscreen", handleBegin);
      video.removeEventListener("webkitendfullscreen", handleEnd);
    };
  }, [retryKey, resolvedUrl]);

  if (hasError) {
    return (
      <div 
        className={`video-player-container no-metadata-link relative bg-gray-900 rounded-lg flex items-center justify-center ${className}`}
        style={{ width, height, maxHeight, marginTop: "0px", marginBottom: "0px" }}
      >
        <div className="text-center text-white p-4">
          <button
            onClick={retryLoading}
            className="p-3 hover:bg-white hover:bg-opacity-10 rounded-full transition-all group"
            aria-label="Retry loading video"
          >
            <RotateCcw className="w-8 h-8 opacity-60 group-hover:opacity-100 transition-opacity" />
          </button>
          <p className="text-sm opacity-80 mt-2">Failed to load, try reloading</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`video-player-container no-metadata-link relative bg-transparent overflow-hidden group cursor-pointer ${className} ${
        isFullscreen ? "fixed inset-0 z-50 rounded-none" : ""
      }`}
      style={{ width, height, maxHeight, marginTop: "0px", marginBottom: "0px" }}
      onMouseMove={handleMouseMove}
      onClick={handleContainerClick}
    >
      {/* Video Player */}
      <ReactPlayer
        key={retryKey} // Force remount on retry
        ref={playerRef}
        url={resolvedUrl}
        playing={playing}
        volume={volume}
        muted={isMuted}
        loop={loop}
        playbackRate={playbackRate}
        width="100%"
        height="100%"
        progressInterval={250} // Reduced frequency since we use requestAnimationFrame for smooth updates
        onReady={handleReady}
        onError={handleError}
        onProgress={handleProgress}
        onDuration={handleDuration}
        onBuffer={handleBuffer}
        onBufferEnd={handleBufferEnd}
        onEnded={handleEnded}
        onPlay={() => {
          // Keep UI state aligned to actual playback (important for iOS fullscreen exit).
          setPlaying(true);
        }}
        onPause={() => {
          // Keep UI state aligned to actual playback (e.g., iOS may pause on fullscreen exit).
          setPlaying(false);
        }}
        config={{
          file: {
            attributes: {
              style: {
                width: width,
                height: height,
                objectFit: objectFit,
                marginTop: "0px",
                marginBottom: "0px",
              },
              poster: thumbnail,
              playsInline: true, // Important for iOS
              "webkit-playsinline": "true", // Legacy iOS support
            },
          },
        }}
      />

      {/* Loading Spinner */}
      {isLoading && (
        <div className="video-player-loading absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <Loader2 className="w-8 h-8 text-white animate-spin" />
        </div>
      )}

      {/* Play/Pause Button Overlay */}
      {!playing && isReady && (
        <div className="video-player-play-button absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 transition-opacity">
          <div className="rounded-[50%] p-6 hover:bg-white hover:bg-opacity-20 transition-all">
            <TriangularPlayButton className="w-12 h-12 text-white" />
          </div>
        </div>
      )}

      {/* Custom Controls */}
      {controls && isReady && (
        <div 
          className={`video-player-controls absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/70 to-transparent p-4 transition-opacity duration-300 ${
            showControls || !playing ? "opacity-100" : "opacity-0"
          }`}
        >
          {/* Progress Bar */}
          <div className="mb-3">
            <div
              className="video-player-progress-bar w-full h-2 bg-white bg-opacity-30 rounded-full cursor-pointer group relative overflow-hidden"
              onClick={handleProgressClick}
            >
              <div
                className={`absolute top-0 left-0 h-full w-full bg-white rounded-full origin-left group-hover:h-2 ${
                  isSeeking ? "transition-none" : "transition-all duration-100 ease-linear"
                }`}
                style={{ 
                  transform: `scaleX(${progress}) translateZ(0)`,
                  willChange: "transform" // Optimize for transform changes
                }}
              />
            </div>
          </div>

          {/* Controls Row */}
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center space-x-3">
              {/* Play/Pause */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newPlayingState = !playing;
                  setPlaying(newPlayingState);
                  setUserPaused(!newPlayingState); // Track if user manually paused
                  
                  // Track play/pause from controls
                  if (newPlayingState) {
                    trackVideoPlayerAction.play(resolvedUrl, "click");
                  } else {
                    trackVideoPlayerAction.pause(resolvedUrl, "click", currentTime);
                  }
                  
                  showControlsTemporarily();
                }}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              >
                {playing ? <Pause className="w-5 h-5" /> : <TriangularPlayButton className="w-5 h-5 text-white" />}
              </button>

              {/* Playback Speed */}
              <button
                onClick={togglePlaybackRate}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors relative"
              >
                <FastForward className="w-5 h-5" />
                {playbackRate === 2 && (
                  <span className="absolute -bottom-0 -right-1 text-xs font-bold bg-white text-black rounded-full w-4 h-4 flex items-center justify-center">
                    2x
                  </span>
                )}
              </button>

              {/* Volume */}
              <button
                onClick={toggleMute}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>

              {/* Restart */}
              <button
                onClick={restartVideo}
                className="video-player-restart-button p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                aria-label="Restart video"
                type="button"
              >
                <RotateCcw className="w-5 h-5" />
              </button>

              {/* Time Display */}
              <div className="text-sm font-medium">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Fullscreen */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  void requestFullscreenToggle();
                }}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              >
                {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer; 